import { toast } from '@bozic/ui/toast';
import { isRejectedWithValue, type Middleware } from '@reduxjs/toolkit';

import { Logger } from '@/shared/logger';
import { mapErrorMessage } from '@/shared/utils/map-error-message';

import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

// Statuses handled elsewhere, so the global logger stays quiet on them:
//   401 → baseQueryWithReauth (silent renew + retry, then sign-out)
//   403 / 404 → expected; the calling screen renders them inline
const SILENCED_STATUSES = new Set<number | string>([401, 403, 404]);

// APIs whose failures must never toast or reach Sentry: the whatsNew docs feed is
// non-essential (external docs host, mounts on every boot) and its sidebar already
// degrades via `isError`. Matched on the action-type prefix (the api's reducerPath)
// because shared code can't import the feature's api module (dependency direction).
const SILENCED_REDUCER_PATHS = new Set(['whatsNewApi']);

interface RtkQueryArgMeta {
  arg?: { type?: 'query' | 'mutation'; endpointName?: string };
}

// a silenced status with a non-JSON body (proxy/gateway error page) arrives as
// PARSING_ERROR carrying the real status in originalStatus — judge by that
const effectiveStatusOf = (
  payload: FetchBaseQueryError | undefined
): number | string | undefined =>
  payload?.status === 'PARSING_ERROR' ? payload.originalStatus : payload?.status;

const isSilenced = (actionType: unknown, payload: FetchBaseQueryError | undefined): boolean => {
  const reducerPath = typeof actionType === 'string' ? actionType.split('/')[0] : '';
  return (
    SILENCED_STATUSES.has(effectiveStatusOf(payload) as number) ||
    SILENCED_REDUCER_PATHS.has(reducerPath)
  );
};

// FETCH_ERROR's `error` field is a raw transport string ("TypeError: Failed to
// fetch") — never user-facing copy; everything else may carry a server message
const toastMessage = (payload: FetchBaseQueryError | undefined): string =>
  payload?.status === 'FETCH_ERROR'
    ? 'Unable to reach the server. Check your connection and try again.'
    : mapErrorMessage(payload, 'Something went wrong loading data');

/**
 * Global RTK Query error logger. Surfaces *unexpected query* failures with a toast +
 * Sentry log so read paths don't each need a try/catch. Mutations are intentionally
 * left alone — they own their success/error messaging via `.unwrap()` + try/catch
 * (error-handling skill: hybrid model).
 */
export const rtkQueryErrorLogger: Middleware = () => next => action => {
  if (isRejectedWithValue(action)) {
    const { arg } = action.meta as RtkQueryArgMeta;
    const payload = action.payload as FetchBaseQueryError | undefined;

    if (arg?.type === 'query' && !isSilenced(action.type, payload)) {
      Logger.trace('RTK Query request failed', {
        level: 'Error',
        tag: 'rtkQuery',
        sentryLogging: true,
        extra: { endpoint: arg.endpointName, status: payload?.status }
      });
      toast.error(toastMessage(payload));
    }
  }

  return next(action);
};
